import React, { lazy, Suspense, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Plane, ShieldCheck, Star } from "lucide-react";

const Globe3D = lazy(() => import("@/components/Globe3D"));

export default function Hero() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-32 pb-12"
    >
      {/* Bright atmospheric backdrop */}
      <div className="absolute inset-0 gradient-hero" />
      <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full opacity-50" style={{ background: "radial-gradient(circle, rgba(0,153,204,0.18), transparent 60%)", filter: "blur(40px)" }} />
      <div className="absolute -bottom-40 -right-20 w-[640px] h-[640px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.18), transparent 60%)", filter: "blur(40px)" }} />

      {/* subtle dot grid */}
      <div className="absolute inset-0 opacity-[0.18] pointer-events-none" style={{
        backgroundImage: "radial-gradient(rgba(0,51,102,0.25) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }} />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center min-h-[calc(100vh-8rem)]">
        {/* Left content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 max-w-2xl"
        >
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-navy/10 mb-7 soft-shadow"
            data-testid="hero-badge"
          >
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, k) => (
                <Star key={k} className="w-3 h-3 fill-gold text-gold" />
              ))}
            </div>
            <span className="text-[11px] uppercase tracking-[0.22em] text-navy-deep font-semibold">
              4.9 · Trusted Since 2014
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tighter text-navy-deep"
            data-testid="hero-heading"
          >
            Your Gateway To{" "}
            <span className="text-gradient-navy">International Travel</span>{" "}
            <span className="text-slate-400 font-light">&</span>{" "}
            <span className="text-gradient-gold">US Visas</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 1 }}
            className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl"
            data-testid="hero-subheading"
          >
            Tour packages, student visas, business visas, B1/B2 appointments, documentation support, and international holiday planning — all under one trusted roof.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-3.5"
          >
            <button
              data-testid="hero-cta-visa"
              onClick={() => scrollTo("contact")}
              className="group relative inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full gradient-navy text-white font-semibold tracking-tight premium-shadow hover:shadow-[0_24px_48px_-12px_rgba(0,51,102,0.45)] hover:scale-[1.02] transition-all"
            >
              Apply For US Visa
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-packages"
              onClick={() => scrollTo("destinations")}
              className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full bg-white text-navy-deep font-semibold tracking-tight border-2 border-navy-deep/12 hover:border-sky-brand/60 hover:bg-sky-soft/40 transition-all soft-shadow"
            >
              <Plane className="w-4 h-4 text-sky-brand -rotate-45" />
              Explore Tour Packages
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="mt-12 flex flex-wrap items-center gap-6 md:gap-8"
            data-testid="hero-mini-stats"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-sky-soft flex items-center justify-center">
                <ShieldCheck className="w-4.5 h-4.5 text-sky-brand" strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-heading text-base font-bold text-navy-deep leading-none">100% Refund</div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mt-1">If Visa Denied*</div>
              </div>
            </div>
            <div className="w-px h-10 bg-navy/10 hidden sm:block" />
            <div>
              <div className="font-heading text-xl font-bold text-navy-deep leading-none">500+</div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-500 mt-1">Visas Approved</div>
            </div>
            <div className="w-px h-10 bg-navy/10 hidden sm:block" />
            <div>
              <div className="font-heading text-xl font-bold text-sky-brand leading-none">25+</div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-500 mt-1">Destinations</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right: Globe panel */}
        <div className="relative h-[440px] sm:h-[540px] lg:h-[660px] w-full">
          {/* navy panel for globe */}
          <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden gradient-navy premium-shadow">
            {/* stars */}
            <div className="absolute inset-0" style={{
              backgroundImage:
                "radial-gradient(1px 1px at 20px 30px, rgba(255,255,255,0.9), transparent), radial-gradient(1px 1px at 90px 40px, rgba(0,200,255,0.9), transparent), radial-gradient(1px 1px at 160px 80px, rgba(255,215,0,0.7), transparent), radial-gradient(1px 1px at 200px 30px, rgba(255,255,255,0.7), transparent)",
              backgroundSize: "220px 130px",
              backgroundRepeat: "repeat",
              opacity: 0.5,
            }} />
            <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 55%, rgba(0,153,204,0.25), transparent 60%)" }} />

            {mounted && (
              <Suspense fallback={<GlobeFallback />}>
                <Globe3D />
              </Suspense>
            )}

            {/* Orbiting label */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative w-[88%] h-[88%] animate-spin-slow">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex items-center justify-center">
                  <div className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur flex items-center gap-2 shadow-lg">
                    <Plane className="w-3.5 h-3.5 text-sky-brand -rotate-45" strokeWidth={2.5} />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-navy-deep font-bold">In Flight</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating chips */}
          <div className="absolute -top-4 -left-3 md:-left-6 hidden sm:block">
            <div className="bg-white rounded-2xl px-4 py-3 animate-float-slow premium-shadow border border-navy/8">
              <div className="text-[9.5px] uppercase tracking-[0.22em] text-gold-dark font-semibold mb-1">Next Departure</div>
              <div className="font-heading text-sm font-bold text-navy-deep">DEL → JFK · 14h 20m</div>
            </div>
          </div>
          <div className="absolute -bottom-4 -right-3 md:-right-6 hidden sm:block">
            <div className="bg-white rounded-2xl px-4 py-3 animate-float-slow premium-shadow border border-navy/8" style={{ animationDelay: "1.5s" }}>
              <div className="text-[9.5px] uppercase tracking-[0.22em] text-sky-brand font-semibold mb-1">Live Route</div>
              <div className="font-heading text-sm font-bold text-navy-deep">BOM → DXB · 3h 10m</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GlobeFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-64 h-64 rounded-full border border-sky-brand/40 animate-pulse-soft" />
    </div>
  );
}
